import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import {
  ControlsRow,
  CustomPlaybackIndicator,
  InfoRow,
  PlayButton,
  PlayerContainer,
  Slider,
  SliderContainer,
  SliderHighlight,
  TimeDisplay,
  VolumeButton,
  VolumeContainer,
  VolumeSlider,
  LoaderSpinner,
} from "./audio-player.styles";
import { ToggleSwitch } from "./toggle-switch";

import MainglobalAudioState from "../hooks/audio-controller";

interface AudioPlayerProps {
  audioUrl: string;
  highlightStart: number;
  highlightEnd: number;
  duration: number;
}
let globalAudioState = MainglobalAudioState;

export default function AudioPlayer({
  audioUrl,
  highlightStart,
  highlightEnd,
  duration,
}: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(highlightStart);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [audioDuration, setAudioDuration] = useState(duration);
  const [userSeeked, setUserSeeked] = useState(false);
  const [wasPaused, setWasPaused] = useState(false);
  const [playFullPodcast, setPlayFullPodcast] = useState(false);
  const isHighlightPlaying = useRef(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sliderRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handleLoaded = () => {
      setAudioDuration(audio.duration || duration); // Fallback to provided duration if needed
      setIsLoaded(true);
      setIsLoading(false);
      
      // Set initial position based on playFullPodcast toggle state
      const initialPosition = playFullPodcast ? 0 : highlightStart;
      audio.currentTime = initialPosition;
      setCurrentTime(initialPosition);
    };

    const handleTimeUpdate = () => {
      const current = audio.currentTime;
      setCurrentTime(current);

      if (
        !playFullPodcast &&
        isHighlightPlaying.current &&
        current >= highlightEnd
      ) {
        audio.pause();
        setIsPlaying(false);
        isHighlightPlaying.current = false;
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setUserSeeked(false);
    };
    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    // Set preload to auto and add error handling for large files
    audio.preload = "metadata";
    audio.onerror = (e) => {
      console.error("Audio loading error:", e);
      // Fallback to using the provided duration if loading fails
      setAudioDuration(duration);
      setIsLoaded(true);
      setIsLoading(false);
    };

    return () => {
      audio.pause();
      if (globalAudioState.audioRef === audio) {
        globalAudioState = { audioRef: null, setIsPlaying: null };
      }
      audio.src = "";
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioUrl, highlightStart, highlightEnd, playFullPodcast, duration]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handlePause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
    setWasPaused(true);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    // Pause any globally playing audio
    if (
      globalAudioState.audioRef &&
      globalAudioState.audioRef !== audioRef.current
    ) {
      globalAudioState.audioRef.pause();
      // Update UI state of the previously playing audio
      if (globalAudioState.setIsPlaying) {
        globalAudioState.setIsPlaying(false);
      }
    }

    // Update global state with current audio
    globalAudioState = {
      audioRef: audioRef.current,
      setIsPlaying: setIsPlaying,
    };

    if (isPlaying) {
      handlePause();
      // Clear global state when pausing
      globalAudioState = { audioRef: null, setIsPlaying: null };
    } else {
      // Set position based on playFullPodcast toggle
      const currentTime = audioRef.current.currentTime;
      
      if (playFullPodcast) {
        // When in full podcast mode, we can play from anywhere
        // No need to adjust position
      } else {
        // When in highlight mode, ensure we're within the highlighted section
        if (currentTime < highlightStart || currentTime > highlightEnd) {
          audioRef.current.currentTime = highlightStart;
          setCurrentTime(highlightStart);
        }
      }

      isHighlightPlaying.current =
        audioRef.current.currentTime >= highlightStart &&
        audioRef.current.currentTime <= highlightEnd;

      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          setWasPaused(false);
        })
        .catch((err: any) => {
          console.warn("Play failed:", err);
          setIsPlaying(false);
        });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const newTime = Number.parseFloat(e.target.value);

    // Prevent seeking outside segment boundaries when toggle is off
    if (!playFullPodcast) {
      if (newTime < highlightStart) {
        audioRef.current.currentTime = highlightStart;
        setCurrentTime(highlightStart);
        setUserSeeked(true);
        return;
      } else if (newTime > highlightEnd) {
        audioRef.current.currentTime = highlightEnd;
        setCurrentTime(highlightEnd);
        setUserSeeked(true);
        return;
      }
    }

    audioRef.current.currentTime = newTime;

    setCurrentTime(newTime);
    setUserSeeked(true);

    isHighlightPlaying.current =
      newTime >= highlightStart && newTime <= highlightEnd;

    // If it was playing before seek, resume playback
    if (isPlaying) {
      setTimeout(() => {
        audioRef?.current?.play().catch((err) => {
          console.warn("Play after seek failed:", err);
          setIsPlaying(false);
        });
      }, 0);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number.parseFloat(e.target.value) / 100;
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMins = `${mins < 10 ? "0" : ""}${mins}`;
    const paddedSecs = `${secs < 10 ? "0" : ""}${secs}`;

    return hrs > 0
      ? `${hrs}:${paddedMins}:${paddedSecs}`
      : `${paddedMins}:${paddedSecs}`;
  };

  const percentStart = (highlightStart / audioDuration) * 100;
  const percentEnd = (highlightEnd / audioDuration) * 100;
  const percentCurrent = (currentTime / audioDuration) * 100;

  return (
    <PlayerContainer>
      <ControlsRow>
        <PlayButton
          onClick={togglePlay}
          disabled={!isLoaded}
          $isPlaying={isPlaying}
          $isLoading={isLoading}
        >
          {isLoading ? (
            <LoaderSpinner />
          ) : isPlaying ? (
            <Pause size={16} />
          ) : (
            <Play size={16} />
          )}
        </PlayButton>

        <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>

        <SliderContainer>
          <SliderHighlight
            $percentStart={percentStart}
            $percentEnd={percentEnd}
            $percentCurrent={percentCurrent}
          />
          <Slider
            ref={sliderRef}
            type="range"
            min={0}
            max={audioDuration}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            disabled={!isLoaded}
          />
        </SliderContainer>

        <TimeDisplay>{formatTime(audioDuration)}</TimeDisplay>

        <VolumeContainer>
          <VolumeButton onClick={toggleMute}>
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </VolumeButton>

          <VolumeSlider
            type="range"
            min={0}
            max={100}
            step={1}
            value={isMuted ? 0 : volume * 100}
            onChange={handleVolumeChange}
          />
        </VolumeContainer>
      </ControlsRow>
      {
        <InfoRow>
          {/* <CustomPlaybackIndicator>Custom playback</CustomPlaybackIndicator> */}
          <ToggleSwitch
            checked={playFullPodcast}
            onChange={(checked: boolean) => {
              // Important: Set state first to ensure all components respect the new toggle state
              setPlayFullPodcast(checked);
              
              if (!audioRef.current) return;
              
              // Stop playback first to prevent unwanted behavior
              const wasPlaying = isPlaying;
              handlePause();
              
              // Apply position change based on toggle state
              // Wait for a tiny delay to ensure state is updated first
              setTimeout(() => {
                // Ensure audioRef.current is still available
                if (!audioRef.current) return;
                
                // If toggle is ON (play full podcast), set time to 0
                // If toggle is OFF (play highlight only), set time to highlightStart
                const newTime = checked ? 0 : highlightStart;
                audioRef.current.currentTime = newTime;
                setCurrentTime(newTime);
                
                // Optionally resume playback if it was playing before
                if (wasPlaying) {
                  audioRef.current.play().catch(err => {
                    console.warn('Play after toggle failed:', err);
                  });
                  setIsPlaying(true);
                }
              }, 50);
            }}
            label="Play full podcast"
          />
        </InfoRow>
      }
    </PlayerContainer>
  );
}
